import { Inject, Injectable } from "@angular/core";
import { IHttpService } from "../IHttpService";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { UrlKey, UrlService } from "../../url/url.service";
import { Observable } from "rxjs";
import { WebLogger } from "../../../web-logger";
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from "../../../environment.config";

@Injectable({
  providedIn: "root",
})
export class HttpSiteminderService implements IHttpService {
  // Cookie entries
  protected readonly sPOID = "cl_spoid"
  protected readonly CCC_SHARED_DATA = "cccSharedData"
  /** These are required Proxy headers **/
  protected readonly ALLOW_ORIGIN_HEADER: string = "Access-Control-Allow-Origin"
  protected readonly URL_TO_FORWARD: string = "X-Url-To-Forward"
  /** These are required Siteminder headers **/
  protected readonly SM_HEADER: string = "SM_AUTHDIRNAME"
  protected readonly SM_DOMAIN: string = "cremsdomain2"
  protected readonly CONSUMER_APPLICATION_CODE: string = "Consumer-Application-Code"
  protected readonly APP_PREFIX: string = "WAH"

  protected readonly CLIENT_ID_HEADER: string = "client_id"

  constructor(
    @Inject(ENVIRONMENT_CONFIG) private readonly config: EnvironmentConfig,
    private readonly httpClient: HttpClient,
    private readonly urlService: UrlService
  ) {}

  doGetRequest<Type>(urlKey: UrlKey, additionalHeaders: object = {}, urlParameters?: object): Observable<any> {
    const requestUrl: string = this.urlService.getUrl(urlKey)
    const proxyUrl: string = this.urlService.getUrl(UrlKey.WEPS_PROXY, urlParameters)

    const headers: HttpHeaders = new HttpHeaders()
      .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
      .set(this.CLIENT_ID_HEADER, this.getCookie())
      .set(this.URL_TO_FORWARD, requestUrl)
      .set(this.SM_HEADER, this.SM_DOMAIN)
      .set(this.CONSUMER_APPLICATION_CODE, this.APP_PREFIX)
    this.applyAdditionalHeaders(headers, additionalHeaders)

    //Make HTTP call and return the generic type provided
    return this.httpClient.get<Type>(proxyUrl, {
      headers: headers,
      withCredentials: true,
    })
  }

  doPostRequest<Type>(urlKey: UrlKey, data: any, additionalHeaders: object = {}, urlParameters?: object): Observable<Type> {
    const requestUrl: string = this.urlService.getUrl(urlKey, urlParameters)
    const proxyUrl: string = this.urlService.getUrl(UrlKey.WEPS_PROXY)

    const headers: HttpHeaders = new HttpHeaders()
      .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
      .set(this.URL_TO_FORWARD, requestUrl)
      .set(this.SM_HEADER, this.SM_DOMAIN)
    this.applyAdditionalHeaders(headers, additionalHeaders)

    WebLogger.log({ message: "HttpSiteminder making POST request", payload: { proxyUrl, requestUrl, dataSize: JSON.stringify(data).length } });
    //Make HTTP call and return the generic type provided
    return this.httpClient.post<Type>(proxyUrl, JSON.stringify(data), {
      headers: headers,
      withCredentials: true,
    })
  }

  doPutRequest<Type>(urlKey: UrlKey, data: any, additionalHeaders: object = {}, urlParameters?: object): Observable<Type> {
    const requestUrl: string = this.urlService.getUrl(urlKey, urlParameters)
    const proxyUrl: string = this.urlService.getUrl(UrlKey.WEPS_PROXY)

    const headers: HttpHeaders = new HttpHeaders()
      .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
      .set(this.URL_TO_FORWARD, requestUrl)
      .set(this.SM_HEADER, this.SM_DOMAIN)
    this.applyAdditionalHeaders(headers, additionalHeaders)

    WebLogger.log({ message: "HttpSiteminder making PUT request", payload: { proxyUrl, requestUrl, dataSize: JSON.stringify(data).length } });
    //Make HTTP call and return the generic type provided
    return this.httpClient.put<Type>(proxyUrl, JSON.stringify(data), {
      headers: headers,
      withCredentials: true,
    })
  }

  // Get POID cookie for external users
  getCookie(): string {
    const cookies = document.cookie.split(";")
    let sPoidCookie = cookies.find((cookie) => cookie.split("=")[0].replace(" ", "") === this.sPOID)
    let cccSharedDataCookie = cookies.find(
      (cookie) => cookie.split("=")[0].replace(" ", "") === this.CCC_SHARED_DATA,
    )
    if (sPoidCookie) return sPoidCookie.split("=")[1]
    else if (cccSharedDataCookie)
      return JSON.parse(decodeURIComponent(cccSharedDataCookie.split("=")[1]))["spoid"]

    WebLogger.log({ message: "HttpSiteminder no sPoid cookie found", level: "error" });
    throw new Error(`No sPoid cookie found`)
  }

  doDeleteRequest<Type>(urlKey: UrlKey, additionalHeaders: object = {}, urlParameters?: object): Observable<any> {
    const requestUrl: string = this.urlService.getUrl(urlKey, urlParameters)
    const proxyUrl: string = this.urlService.getUrl(UrlKey.WEPS_PROXY)

    const headers: HttpHeaders = new HttpHeaders()
      .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
      .set(this.URL_TO_FORWARD, requestUrl)
      .set(this.SM_HEADER, this.SM_DOMAIN)
    this.applyAdditionalHeaders(headers, additionalHeaders)

    return this.httpClient.delete<Type>(proxyUrl, {
      headers: headers,
      withCredentials: true,
    })
  }

  applyAdditionalHeaders(headers: HttpHeaders, additionalHeaders: Object): any {
    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach((key) => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }
  }
  doSimpleGetRequest<Type>(urlKey: UrlKey, urlParameters?: object): Observable<any> {
    const url = this.urlService.getUrl(urlKey, urlParameters);
    return this.httpClient.get<Type>(url);
  }
}
